import { Resource } from "@casablanca/casablanca.contracts.iam-client-ts-v1";
import { Pilet, PiletEntries } from "piral-core";
import resourceApi from "./apis/iam/resourceApi";
import { env } from "./environment";
import { acquireToken, getCurrentUser } from "./msal";
import { getCurrentTenantFromStorage, setCurrentTenant } from "./tenant";

function getFeedUrl(): string {
  const feedUrl = env.PIRAL_FEED_URL;
  // feedUrl = 'https://feed.piral.cloud/api/v1/pilet/tractor-v2-demo'; //demo
  return feedUrl;
}

async function requestPilets(): Promise<Pilet[]> {
  const token = await acquireToken();

  let currentTenant = await getCurrentTenantFromStorage();
  const user = await getCurrentUser();
  let tenants: Resource[] = [];
  const tenantResponse = await resourceApi.getUsersTenants();
  tenants = tenantResponse.values;

  if (currentTenant) {
    const hasAccess = tenants.some(
      (tenant) =>
        tenant.casablancaId.toLowerCase() === currentTenant.id.toLowerCase()
    );
    if (!hasAccess) {
      localStorage.removeItem("currentTenant");
      currentTenant = null;
    }
  }

  let tenantIdFromRoute: string | null = null;
  if (
    !window.location.pathname.startsWith("/iam/") &&
    !window.location.pathname.startsWith("/iam-user/") &&
    !window.location.pathname.startsWith("/app/") &&
    window.location.pathname !== "/"
  ) {
    tenantIdFromRoute = window.location.pathname.split("/")[1].toLowerCase();
  }

  if (!currentTenant && tenantIdFromRoute) {
    const tenant = await resourceApi.getTenantByCasablancaId(tenantIdFromRoute);
    const organizationId = await resourceApi.getOrganizationIdByCasablancaId(
      user.b2cObjectId,
      tenantIdFromRoute
    );
    currentTenant = {
      id: tenant.casablancaId,
      name: tenant.displayName,
      organizationId: organizationId,
    };
    setCurrentTenant(currentTenant);
  }
  if (!currentTenant) {
    if (tenants && tenants.length === 1) {
      const tenant = tenants[0];
      const organizationId = await resourceApi.getOrganizationIdByCasablancaId(
        user.b2cObjectId,
        tenant.casablancaId
      );
      currentTenant = {
        id: tenant.casablancaId,
        name: tenant.displayName,
        organizationId: organizationId,
      };
      setCurrentTenant(currentTenant);
    }
  }

  let tenant = currentTenant;
  if (window.location.pathname != "/iam/resourceselection" && !tenant) {
    window.location.href = `/iam/resourceselection`;
  }

  // Tenant access validation is now handled above

  const headers: Record<string, string> = {};

  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  if (tenant) {
    headers["x-tenantid"] = tenant.id;
    headers["x-organizationid"] = tenant.organizationId;
  }

  const response = await fetch(getFeedUrl(), {
    headers: headers,
  });

  if (!response.ok) {
    throw new Error("Failed to fetch pilets");
  }

  const json = await response.json();
  const pilets = json.items as Pilet[];
  return pilets;
}

async function redirectToFirstApp(loadedPilets: PiletEntries) {
  const tenant = await getCurrentTenantFromStorage();
  const cloudUiPilet = loadedPilets.find((x) => x.name === "@casablanca/cloud-ui");
  const newAppBaseUrl: string | undefined = (cloudUiPilet as any)?.config?.["NewApp:BaseUrl"];
  const housekeepingPilet = loadedPilets.find(
    (x) => x.name === "@casablanca/housekeeping"
  );
  const dashboardPilet = loadedPilets.find(
    (x) => x.name === "@casablanca/dashboard"
  );
  const firstPilet = loadedPilets
    .filter((x) => x.name !== "@casablanca/cloud-ui")
    .filter((x) => x.name !== "@casablanca/iam")
    .filter((x) => x.name !== "@casablanca/iam-user")
    .filter((x) => x.name !== "@casablanca/licensing")
    .filter((x) => x.name !== "@casablanca/legacy")[0];

  // Preserve all query params so they survive the redirect.
  const querySuffix = window.location.search || "";

  if (!tenant) {
    window.location.href = `/iam/resourceselection`;
    return;
  } else if (housekeepingPilet) {
    window.location.href = `/${tenant.id}/${(housekeepingPilet as any).config.identifier}${querySuffix}`;
    return;
  } else if (newAppBaseUrl) {
    const baseUrl = newAppBaseUrl.replace(/\/$/, '');
    window.location.href = `${baseUrl}/${tenant.id}${querySuffix}`;
    return;
  } else if (dashboardPilet) {
    window.location.href = `/${tenant.id}/${(dashboardPilet as any).config.identifier}${querySuffix}`;
    return;
  } else if (firstPilet) {
    window.location.href = `/${tenant.id}/${(firstPilet as any).config.identifier}${querySuffix}`;
    return;
  }
}

export { redirectIfAppUnavailable, redirectToFirstApp, requestPilets };

async function redirectIfAppUnavailable(loadedPilets: PiletEntries) {
  // Extract /{tenantId}/{appIdentifier} from the current path
  const parts = window.location.pathname.split("/").filter(Boolean);
  if (parts.length < 2) return;

  const appIdentifier = parts[1].toLowerCase();

  // Skip framework/infra routes that are not pilet-driven
  const skipPrefixes = ["iam", "iam-user", "authentication", "logout", "app"];
  if (skipPrefixes.some((p) => appIdentifier.startsWith(p))) return;

  // Check if any loaded pilet covers this identifier
  const isLoaded = loadedPilets.some(
    (x) =>
      (x as any).config?.identifier?.toLowerCase() === appIdentifier
  );
  if (isLoaded) return;

  // App not available for this user — redirect to new app if configured
  const cloudUiPilet = loadedPilets.find((x) => x.name === "@casablanca/cloud-ui");
  const newAppBaseUrl: string | undefined = (cloudUiPilet as any)?.config?.["NewApp:BaseUrl"];
  if (!newAppBaseUrl) return;

  const tenant = await getCurrentTenantFromStorage();
  const baseUrl = newAppBaseUrl.replace(/\/$/, "");
  window.location.href = tenant ? `${baseUrl}/${tenant.id}` : baseUrl;
}
